import {flattenErrorResponse, useIDTokenProviderNames, useSimpleTable} from '..';
import React, {useCallback, useContext, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {
    Button,
    ButtonGroup,
    ContextualMenu,
    Modal,
    OptionInterface,
    Select,
    SelectOption,
    Table,
    TableData,
    TextBox,
    Tooltip,
    useToast,
} from '@veneer/core';

import {ResponseError, Role, TenantPermission, TenantUser} from '@br/idm-api';
import {useCustomerApi, useTenantUser} from '@br/idm-components';

import {useIntlFormatter} from '../i18n';
import {GlobalTranslationsContext} from '../i18n/global';
import {assertIsNotNullish} from '../util/types';
import {shouldShowRole} from '../util/users';

const AddUser = ({
    showAddUser,
    roles,
    onAdd,
    onClose,
}: {
    showAddUser: boolean;
    roles: Role[];
    onAdd: () => void;
    onClose: () => void;
}) => {
    const {t} = useTranslation(['users']);
    const {addToast} = useToast();
    const {tenantId} = useTenantUser();
    const {tenantApi} = useCustomerApi();
    const {veneer: veneerTranslations} = useContext(GlobalTranslationsContext);

    const [showAddModal, setShowAddModal] = useState(false);
    const [isAdding, setIsAdding] = useState(false);

    const [email, setEmail] = useState('');
    const [userRoles, setUserRoles] = useState<string[]>([]);

    const [emailErrors, setEmailErrors] = useState<string[]>([]);

    useEffect(() => {
        setEmailErrors([]);
        setShowAddModal(showAddUser);
        setEmail('');
        setUserRoles([]);
    }, [showAddUser]);

    const handleRoleChange = useCallback((option: SelectOption<string>) => {
        setUserRoles([option.value]);
    }, []);

    const addUser = useCallback(() => {
        setIsAdding(true);
        setEmailErrors([]);
        tenantApi
            .tenantUserCreate({
                tenantId,
                anticipatoryTenantUserRequest: {
                    email: email,
                    roles: userRoles,
                },
            })
            .then(
                () => {
                    addToast({
                        id: `userAdd:${email}`,
                        type: 'positive',
                        text: t('users:added', '{{email}} added.', {email: email}),
                    });
                    onAdd();
                    onClose();
                },
                async (error: ResponseError) => {
                    setEmailErrors(await flattenErrorResponse(error));
                }
            )
            .finally(() => {
                setIsAdding(false);
            });
    }, [tenantApi, tenantId, email, userRoles, addToast, t, onAdd, onClose]);

    return (
        <Modal
            id="add-user-modal"
            onClose={onClose}
            show={showAddModal}
            title={t('users:addUserTitle', 'Add Account')}
            footer={
                <ButtonGroup>
                    <Button
                        data-testid="save-add"
                        appearance="primary"
                        loading={isAdding}
                        onClick={addUser}
                    >
                        {t('users:addUserButton', 'Save')}
                    </Button>
                    <Button
                        data-testid="cancel-add"
                        appearance="secondary"
                        disabled={isAdding}
                        onClick={onClose}
                    >
                        {t('users:cancelAddButton', 'Cancel')}
                    </Button>
                </ButtonGroup>
            }
        >
            <TextBox
                value={email}
                id="user_email"
                label={t('users:email', 'Email')}
                onChange={setEmail}
                helperText={emailErrors.join('\n')}
                error={emailErrors.length > 0}
            />
            <Select
                data-testid="role-dropdown"
                className="mt-4"
                options={roles
                    .filter(role => shouldShowRole({user: null, role}))
                    .map(role => ({
                        value: role.id,
                        label: role.name,
                    }))}
                clearIcon={false}
                id="user_roles"
                label={t('users:roles', 'Roles')}
                value={userRoles}
                onChange={handleRoleChange}
                i18n={veneerTranslations.select}
            />
        </Modal>
    );
};

export const EditUser = ({
    user,
    roles,
    onEdit,
    onClose,
}: {
    user: TenantUser | null;
    roles: Role[];
    onEdit: () => void;
    onClose: () => void;
}) => {
    const {t} = useTranslation(['users']);
    const {addToast} = useToast();
    const {tenantId} = useTenantUser();
    const {tenantApi} = useCustomerApi();
    const {veneer: veneerTranslations} = useContext(GlobalTranslationsContext);

    const [userRoles, setUserRoles] = useState<string[]>([]);
    const [showEditModal, setShowEditModal] = useState(false);
    const [isEditing, setIsEditing] = useState(false);

    useEffect(() => {
        if (user !== null) {
            setUserRoles([...user.roles]);
            setShowEditModal(true);
        } else {
            setShowEditModal(false);
            setUserRoles([]);
        }
    }, [user]);

    const handleRoleChange = useCallback((option: SelectOption<string>) => {
        setUserRoles([option.value]);
    }, []);

    const editUser = useCallback(() => {
        assertIsNotNullish(user);
        setIsEditing(true);
        tenantApi
            .tenantUserPartialUpdate({
                tenantId,
                id: user.id,
                patchedTenantUserRequest: {
                    roles: userRoles,
                },
            })
            .then(
                () => {
                    addToast({
                        id: `userEdit:${user.id}`,
                        type: 'positive',
                        text: t('users:updated', '{{email}} updated.', {email: user.email}),
                    });
                    onEdit();
                },
                async (error: ResponseError) => {
                    const errors = await flattenErrorResponse(error);
                    errors.forEach(error =>
                        addToast({
                            id: error,
                            type: 'negative',
                            text: error,
                        })
                    );
                }
            )
            .finally(() => {
                onClose();
                setIsEditing(false);
            });
    }, [tenantApi, tenantId, user, userRoles, addToast, t, onEdit, onClose]);

    return (
        <Modal
            id="edit-user-modal"
            onClose={onClose}
            show={showEditModal}
            title={t('users:editUserTitle', 'Edit user')}
            footer={
                <ButtonGroup>
                    <Button
                        data-testid="save-edit"
                        appearance="primary"
                        loading={isEditing}
                        onClick={editUser}
                    >
                        {t('users:editButton', 'Save')}
                    </Button>
                    <Button
                        data-testid="cancel-edit"
                        appearance="secondary"
                        disabled={isEditing}
                        onClick={onClose}
                    >
                        {t('users:cancelEditButton', 'Cancel')}
                    </Button>
                </ButtonGroup>
            }
        >
            <Select
                className="mt-4"
                options={roles
                    .filter(role => shouldShowRole({user, role}))
                    .map(role => ({
                        value: role.id,
                        label: role.name,
                    }))}
                clearIcon={false}
                data-testid="user_type-edit"
                id="user_roles"
                label={t('users:roles', 'Roles')}
                value={userRoles}
                onChange={handleRoleChange}
                i18n={veneerTranslations.select}
            />
        </Modal>
    );
};

export const DeleteUser = ({
    user,
    onDelete,
    onClose,
}: {
    user: TenantUser | null;
    onDelete: () => void;
    onClose: () => void;
}) => {
    const {t} = useTranslation(['users']);
    const {addToast} = useToast();
    const {tenantId} = useTenantUser();
    const {tenantApi} = useCustomerApi();

    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    useEffect(() => {
        setShowDeleteModal(user !== null);
    }, [user]);

    const deleteUser = useCallback(() => {
        assertIsNotNullish(user);
        setIsDeleting(true);
        tenantApi
            .tenantUserDestroy({tenantId, id: user.id})
            .then(
                () => {
                    addToast({
                        id: `userDelete:${user.id}`,
                        type: 'positive',
                        text: t('users:deleted', '{{email}} deleted.', {email: user.email}),
                    });
                    onDelete();
                },
                async (error: ResponseError) => {
                    const errors = await flattenErrorResponse(error);
                    errors.forEach(error =>
                        addToast({
                            id: error,
                            type: 'negative',
                            text: error,
                        })
                    );
                }
            )
            .finally(() => {
                onClose();
                setIsDeleting(false);
            });
    }, [tenantApi, t, user, onClose, onDelete, tenantId, addToast]);

    return (
        <Modal
            id="delete-user-modal"
            onClose={onClose}
            show={showDeleteModal}
            title={t('users:confirmDeleteTitle', 'Confirm account deletion')}
            footer={
                <ButtonGroup>
                    <Button
                        appearance="primary"
                        colorScheme="negative"
                        loading={isDeleting}
                        onClick={deleteUser}
                    >
                        {t('users:deleteButton', 'Delete')}
                    </Button>
                    <Button
                        appearance="secondary"
                        disabled={isDeleting}
                        onClick={onClose}
                    >
                        {t('users:cancelDeleteButton', 'Cancel')}
                    </Button>
                </ButtonGroup>
            }
        >
            <p>
                {user
                    ? t('users:confirmDelete', 'Are you sure you want to delete {{email}}?', {email: user.email})
                    : null}
            </p>
        </Modal>
    );
};

const UserCommand = {
    edit: 0,
    delete: 1,
} as const;
type UserCommand = (typeof UserCommand)[keyof typeof UserCommand];

const Users = ({filterInvites = false}: {filterInvites?: boolean}): JSX.Element => {
    const {t} = useTranslation(['users', 'translation']);
    const {roleApi, tenantApi} = useCustomerApi();
    const {veneer: veneerTranslations} = useContext(GlobalTranslationsContext);
    const {formatDateTime} = useIntlFormatter();
    const idTokenProviderNames = useIDTokenProviderNames();

    const tenantUser = useTenantUser();
    const {tenantId} = tenantUser;

    const [roles, setRoles] = useState<Role[]>([]);

    const [userToDelete, setUserToDelete] = useState<TenantUser | null>(null);
    const [userToEdit, setUserToEdit] = useState<TenantUser | null>(null);
    const [showAddUser, setShowAddUser] = useState(false);

    const fetchUsers = useCallback(() => {
        return tenantApi.tenantUserList({tenantId});
    }, [tenantId, tenantApi]);

    const {table, data, refresh} = useSimpleTable(fetchUsers);

    useEffect(() => {
        void roleApi.roleList().then(setRoles);
    }, [tenantId, tenantApi, roleApi]);

    const canCreateUsers = useMemo(() => tenantUser.hasPermission(TenantPermission.CreateUsers), [tenantUser]);
    const canEditUsers = useMemo(() => tenantUser.hasPermission(TenantPermission.UpdateUsers), [tenantUser]);
    const canDeleteUsers = useMemo(() => tenantUser.hasPermission(TenantPermission.DeleteUsers), [tenantUser]);

    const options: OptionInterface<UserCommand>[] = useMemo(() => {
        const options: OptionInterface<UserCommand>[] = [];
        if (canEditUsers) {
            options.push({
                value: UserCommand.edit,
                label: t('users:edit', 'Edit'),
            });
        }
        if (canDeleteUsers) {
            options.push({
                value: UserCommand.delete,
                label: t('users:delete', 'Delete'),
            });
        }
        return options;
    }, [canEditUsers, canDeleteUsers, t]);

    const handleAction = useCallback((action: UserCommand, user: TenantUser) => {
        switch (action) {
            case UserCommand.delete:
                setUserToDelete(user);
                break;
            case UserCommand.edit:
                setUserToEdit(user);
                break;
        }
    }, []);

    const notYetLoggedIn = useMemo(
        () => (
            <Tooltip
                arrow
                placement="top"
                content={t('users:notYetLoggedIn', 'Not yet logged in.')}
            >
                <span>{t('translation:ellipsis', '…')}</span>
            </Tooltip>
        ),
        [t]
    );

    const localUser = t('users:local', 'Local');

    const userData: TableData[] = useMemo(() => {
        const filteredData = filterInvites ? data.filter(tenantUser => !tenantUser.user?.lastLogin) : data;
        return filteredData.map(tenantUser => {
            const {user} = tenantUser;
            return {
                email: tenantUser.email,
                lastName: user ? user.lastName : notYetLoggedIn,
                firstName: user ? user.firstName : notYetLoggedIn,
                created: formatDateTime(tenantUser.createdAt),
                lastLogin: user?.lastLogin ? formatDateTime(user.lastLogin) : notYetLoggedIn,
                roles: tenantUser.roles
                    .map((roleId: string) => roles.find(role => role.id === roleId))
                    .filter(Boolean)
                    .map(role => role?.name)
                    .join(', '),
                loginProvider: user
                    ? user.loginProvider !== null
                        ? idTokenProviderNames[user.loginProvider]
                        : localUser
                    : notYetLoggedIn,
                rowConfig: {
                    action:
                        options.length > 0 && (!tenantUser.user || tenantUser.user.locallyManaged) ? (
                            <ContextualMenu
                                data-testid="action-menu"
                                options={options}
                                // eslint-disable-next-line react/jsx-no-bind
                                onClick={(e, option) => handleAction(option.value, tenantUser)}
                            />
                        ) : undefined,
                },
            };
        });
    }, [
        data,
        roles,
        options,
        notYetLoggedIn,
        handleAction,
        formatDateTime,
        idTokenProviderNames,
        localUser,
        filterInvites,
    ]);

    const columns = useMemo(() => {
        const cols = [
            {
                id: 'email',
                label: t('users:email', 'Email'),
            },
            {
                id: 'lastName',
                label: t('users:lastName', 'Last Name'),
            },
            {
                id: 'firstName',
                label: t('users:firstName', 'First Name'),
            },
            {
                id: 'created',
                label: t('users:created', 'Created'),
            },
            {
                id: 'roles',
                label: t('users:roles', 'Roles'),
            },
            {
                id: 'lastLogin',
                label: t('users:lastLoggedIn', 'Last Log In'),
            },
            {
                id: 'loginProvider',
                label: t('users:loginProvider', 'Login Provider'),
            },
        ];

        return filterInvites
            ? // Non-empty columns for users that have not yet logged in
              cols.filter(col => ['email', 'created', 'roles'].includes(col.id))
            : cols;
    }, [filterInvites, t]);

    const handleShowAddUser = useCallback(() => setShowAddUser(true), []);
    const handleCloseDelete = useCallback(() => setUserToDelete(null), []);
    const handleCloseEdit = useCallback(() => setUserToEdit(null), []);
    const handleCloseAdd = useCallback(() => setShowAddUser(false), []);

    return (
        <>
            {canCreateUsers ? (
                <div className="d-flex justify-content-end mb-5">
                    <Button
                        appearance="primary"
                        onClick={handleShowAddUser}
                    >
                        {t('users:addUser', 'Add Account')}
                    </Button>
                </div>
            ) : null}

            <Table
                columns={columns}
                data={userData}
                i18n={veneerTranslations.table}
                {...table}
                loading={table.loading || roles.length === 0}
                data-testid="users-table"
            />
            {canDeleteUsers ? (
                <DeleteUser
                    user={userToDelete}
                    onDelete={refresh}
                    onClose={handleCloseDelete}
                />
            ) : null}
            {canEditUsers ? (
                <EditUser
                    user={userToEdit}
                    roles={roles}
                    onEdit={refresh}
                    onClose={handleCloseEdit}
                />
            ) : null}
            {canCreateUsers ? (
                <AddUser
                    showAddUser={showAddUser}
                    roles={roles}
                    onAdd={refresh}
                    onClose={handleCloseAdd}
                />
            ) : null}
        </>
    );
};

export default Users;
